import React, { useCallback } from 'react';
import cn from 'classnames';
import { Spinner } from '@creditflowresearch/uilib/Loader/Spinner';
import Menu from '../components/Menu';
import Input from '../components/Input';
import Option from '../components/Option';
import ValueContainer from '../components/ValueContainer';
import MultiValueContainer from '../components/MultiValueContainer';
import SingleValue from '../components/SingleValue';
import MultiValue from '../components/MultiValue';
import GroupHeading from '../components/GroupHeading';
import Group from '../components/Group';
import MultiValueRemove from '../components/MultiValueRemove';
import Control from '../components/Control';
import ClearIndicator from '../components/ClearIndicator';
import DropdownIndicator from '../components/DropdownIndicator';
import { TSelectProps } from '../interfaces';

import styles from '../select.module.scss';
import groupedStyles from '../groupedSelect.module.scss';

const useSelectComponents = (
  selectProps: TSelectProps,
  selectWrapperRef: React.RefObject<HTMLDivElement>,
  isSelectMenuOpen: boolean,
  isMulti: boolean,
) => {
  const {
    name,
    dataTest,
    isSlim,
    size,
    components = {},
    isUsePortal,
    bodyElement,
    getMenuOuterStyle,
    onInputFocus,
    selectedOptionClassName,
    optionClassName,
    isLoading,
    isGrouped,
    isSelectedPlaceholderVisibleWhenOpen,
    selectedPlaceholderWhenOpen,
    multiValueClassName,
    isHasOneOption,
    scrollableParentElements,
    groupHeadingClassName,
    onGroupHeaderClick,
    menuClassName,
    isUseResponsiveTagsCount,
    invalidValuesTooltipTitle,
    showClearIndicator,
  } = selectProps;

  const MenuComponent = useCallback((props) => (
    <Menu
      ownProps={ props }
      selectWrapper={ selectWrapperRef.current }
      dataTest={ dataTest }
      isUsePortal={ isUsePortal }
      bodyElement={ bodyElement }
      getMenuOuterStyle={ getMenuOuterStyle }
      scrollableParentElements={ scrollableParentElements }
      className={ menuClassName }
    />
  ), [selectWrapperRef.current, dataTest, isUsePortal, bodyElement, getMenuOuterStyle]);

  const InputComponent = useCallback((props) => (
    <Input ownProps={ props } name={ name } onInputFocus={ onInputFocus } />
  ), [name, onInputFocus]);

  const OptionComponent = useCallback((props) => (
    <Option
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
      selectedOptionClassName={ cn(selectedOptionClassName, { [groupedStyles.selectedOption]: isGrouped }) }
      optionClassName={ cn(optionClassName, { [groupedStyles.option]: isGrouped }) }
    />
  ), [dataTest, selectedOptionClassName, optionClassName, components?.Option]);

  const ValueContainerComponent = useCallback((props) => (
    <ValueContainer
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
      isLoading={ isLoading }
      isSlim={ isSlim }
      size={ size }
    />
  ), [dataTest, isLoading, isSlim, size]);

  const SingleValueComponent = useCallback((props) => (
    <SingleValue
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
      isSelectedPlaceholderVisibleWhenOpen={ isSelectedPlaceholderVisibleWhenOpen }
      selectedPlaceholderWhenOpen={ selectedPlaceholderWhenOpen }
      isSelectMenuOpen={ isSelectMenuOpen }
    />
  ), [dataTest, isSelectedPlaceholderVisibleWhenOpen, selectedPlaceholderWhenOpen, isSelectMenuOpen]);

  const MultiValueContainerComponent = useCallback((props) => (
    <MultiValueContainer
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
      isLoading={ isLoading }
      isSlim={ isSlim }
      size={ size }
      isUseResponsiveTagsCount={ isUseResponsiveTagsCount }
    />
  ), [dataTest, isLoading, isSlim, size, isUseResponsiveTagsCount]);

  const MultiValueComponent = useCallback((props) => (
    <MultiValue
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
      multiValueClassName={ cn(multiValueClassName, { [groupedStyles.multiValueWrp]: isGrouped }) }
      isUseResponsiveTagsCount={ isUseResponsiveTagsCount }
      invalidValuesTooltipTitle={ invalidValuesTooltipTitle }
    />
  ), [dataTest, multiValueClassName, components?.MultiValue, isUseResponsiveTagsCount, invalidValuesTooltipTitle]);

  const GroupHeadingComponent = useCallback((props) => (
    <GroupHeading
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
      groupHeadingClassName={ groupHeadingClassName }
      onGroupHeaderClick={ onGroupHeaderClick }
    />
  ), []);
  const GroupComponent = useCallback((props) => (
    <Group ownProps={ props } dataTest={ dataTest } components={ components } />
  ), []);

  const MultiValueRemoveComponent = useCallback((props) => (
    <MultiValueRemove
      ownProps={ props }
      isHasOneOption={ isHasOneOption }
      components={ components }
    />
  ), [isHasOneOption]);

  const ControlComponent = useCallback((props) => (
    <Control
      ownProps={ props }
      dataTest={ dataTest }
      components={ components }
    />
  ), [dataTest, components?.Control]);

  const ClearIndicatorComponent = useCallback((props) => (
    <ClearIndicator
      ownProps={ props }
      components={ components }
      showClearIndicator={ showClearIndicator }
    />
  ), [components?.ClearIndicator, showClearIndicator]);

  const DropdownIndicatorComponent = useCallback((props) => (
    <DropdownIndicator ownProps={ props } components={ components } />
  ), [components?.DropdownIndicator]);

  return {
    components: {
      ...components,
      Menu: MenuComponent,
      Input: InputComponent,
      Option: OptionComponent,
      ValueContainer: isMulti ? MultiValueContainerComponent : ValueContainerComponent,
      SingleValue: SingleValueComponent,
      MultiValue: MultiValueComponent,
      LoadingIndicator: () => <Spinner className={ styles.selectSpinner } />,
      MultiValueRemove: MultiValueRemoveComponent,
      Control: ControlComponent,
      ClearIndicator: ClearIndicatorComponent,
      DropdownIndicator: DropdownIndicatorComponent,
      ...(isGrouped ? { GroupHeading: GroupHeadingComponent, Group: GroupComponent } : {}),
    },
  };
};

export default useSelectComponents;
